import { Shadow, VStack } from '@genial/components';
import { Utils } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import React, { useContext, useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../routes/types';
import { useCustomerDetailsActions } from '../../../../../../store/customerDetail/actions';
import {
  customerAssetProductSelected,
  customersAssetsDateSelected,
  isExportingPdfForCustomersAssets,
} from '../../../../../../store/customerDetail/atom';
import { customerInvestedAssetsQuery } from '../../../../../../store/customerDetail/selector';
import { FinancialDetailsContext } from '../../context';
import { EmptyList } from '../EmptyList';
import { ErrorListing } from '../ErrorListing';
import { AssetCard } from '../shared/AssetCard';
import { AssetLoadingCard } from '../shared/AssetLoadingCard';
import { ProductDistributionHeader } from '../shared/ProductDistributionHeader';

import { emptyListProps } from './constants';

export const InvestedAssets: React.FC = () => {
  const { data: dataFromComposition } = useContext(FinancialDetailsContext);
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const productsOnDetailScreen = useRecoilValue(customerAssetProductSelected);
  const selectedDate = useRecoilValue(customersAssetsDateSelected);
  const isExportingPdf = useRecoilValue(isExportingPdfForCustomersAssets);

  const {
    setAssetsDetailProductSelected,
    downloadCustomerAssetsDetailsPdf,
    setCustomersAssetsDateSelected,
  } = useCustomerDetailsActions();

  const { refresher, data, error, loading } = useRecoilQuery(
    customerInvestedAssetsQuery({ accountNumber: route.params.accountNumber })
  );

  const assets = data?.data.assetsNormalized || [];
  const isToday = !!selectedDate && Utils.Date.IsToday(selectedDate);

  const handleDownloadPdf = () => {
    downloadCustomerAssetsDetailsPdf({
      consolidatedDate: selectedDate || new Date(),
      accountId: route.params.accountId,
    });
  };

  const handleSendToClient = () => {
    downloadCustomerAssetsDetailsPdf({
      consolidatedDate: selectedDate || new Date(),
      accountId: route.params.accountId,
      sharingType: 'Email',
    });
  };

  useEffect(() => {
    if (!data?.data.assetsNormalized.length) {
      return;
    }

    const firstProduct = data?.data.assetsNormalized[0];
    const productDetailOnScreen = productsOnDetailScreen[firstProduct.marketTypeNormalized];

    setAssetsDetailProductSelected(
      firstProduct || { ...productDetailOnScreen, valueNormalized: 0, percentageNormalized: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const renderProducts = () => {
    if (error) {
      return <ErrorListing handleReload={refresher} />;
    }

    if (loading) {
      return Array.from({ length: 5 }).map((_, index) => (
        <AssetLoadingCard key={index} index={index} />
      ));
    }

    if (!data?.data.assetsNormalized?.length) {
      return <EmptyList {...emptyListProps} />;
    }

    return (
      <VStack space="stack.nano">
        {assets.map((product) => {
          return (
            <AssetCard key={product.marketNameNormalized} product={product} openDetailsBelow />
          );
        })}
      </VStack>
    );
  };

  return (
    <Shadow level={1} borderRadius="sm" my="inset.sm" paddingX="inline.xxxs" paddingY="inline.xxs">
      <VStack space="stack.xxs">
        <ProductDistributionHeader
          isLoading={loading}
          totalValue={dataFromComposition?.total.totalInvested || 0}
          products={assets}
          hasError={!!error}
          hasPdfExport
          hasDateFilter
          onDownloadPdf={handleDownloadPdf}
          onSendToClient={handleSendToClient}
          onDateSelected={setCustomersAssetsDateSelected}
          selectedDate={selectedDate}
          isExportingPdf={isExportingPdf}
          isToday={isToday}
        />
        {renderProducts()}
      </VStack>
    </Shadow>
  );
};
